import { RefObject, useLayoutEffect, useRef, useState } from 'react'

export type AnchoredMenuAlign = 'top' | 'bottom'

export type AnchoredMenuPosition = {
  top: number
  left: number
  /**
   * False until the menu has been measured. Render the menu hidden while this
   * is false — the first pass has no dimensions to clamp against, so painting
   * it would flash at the unclamped position.
   */
  isMeasured: boolean
}

const VIEWPORT_PADDING = 8

/**
 * Positions a menu immediately to the right of `anchorRef` and clamps it into
 * the viewport. Coordinates are viewport-relative (no scroll offset) because
 * the menu is expected to be portaled to `document.body` and positioned
 * `fixed` — the sidebar that anchors it lives inside an `overflow-y: auto`
 * column, which would otherwise clip an absolutely-positioned child.
 *
 * `align` picks which edge lines up with the anchor: `bottom` for a menu
 * hanging off a row at the foot of the sidebar, `top` for a submenu that
 * should start level with the menu that opened it.
 *
 * A `ResizeObserver` on the menu drives the second pass: the first run has no
 * menu dimensions, so it flips/clamps as soon as real ones arrive.
 */
export function useAnchoredMenuPosition(
  isOpen: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
  align: AnchoredMenuAlign = 'bottom',
  gap = 8
): AnchoredMenuPosition | null {
  const [pos, setPos] = useState<AnchoredMenuPosition | null>(null)
  const rafRef = useRef(0)

  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null)
      return
    }

    const update = () => {
      const anchor = anchorRef.current
      if (!anchor) return

      const a = anchor.getBoundingClientRect()
      const menu = menuRef.current?.getBoundingClientRect()
      const menuW = menu?.width ?? 0
      const menuH = menu?.height ?? 0
      const isMeasured = menuW > 0 && menuH > 0

      let left = a.right + gap
      // Flip to the anchor's left when opening right would overflow.
      if (isMeasured && left + menuW > window.innerWidth - VIEWPORT_PADDING) {
        left = Math.max(VIEWPORT_PADDING, a.left - gap - menuW)
      }

      let top = align === 'bottom' ? a.bottom - menuH : a.top
      if (isMeasured) {
        const maxTop = window.innerHeight - menuH - VIEWPORT_PADDING
        top = Math.min(
          Math.max(VIEWPORT_PADDING, top),
          Math.max(VIEWPORT_PADDING, maxTop)
        )
      }

      setPos((prev) =>
        prev &&
        prev.top === top &&
        prev.left === left &&
        prev.isMeasured === isMeasured
          ? prev
          : { top, left, isMeasured }
      )
    }

    const scheduleUpdate = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(update)
    }

    update()

    window.addEventListener('scroll', scheduleUpdate, true)
    window.addEventListener('resize', scheduleUpdate)

    // ResizeObserver is stubbed in some jsdom setups; guard so tests that do
    // not polyfill it still get the first-pass position.
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleUpdate)
        : null
    if (observer && menuRef.current) observer.observe(menuRef.current)
    if (observer && anchorRef.current) observer.observe(anchorRef.current)

    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('scroll', scheduleUpdate, true)
      window.removeEventListener('resize', scheduleUpdate)
      observer?.disconnect()
    }
  }, [isOpen, anchorRef, menuRef, align, gap])

  return pos
}
